import * as React from 'react';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import DateRangePicker from 'react-bootstrap-daterangepicker';

import classNames from 'classnames';
import $ from 'jquery';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import { MED_DATETIME_NO_YEAR_DISPLAY, formatDateTimeDisplay, momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { SvgButton, color } from '@coursera/coursera-ui';
import { SvgFail } from '@coursera/coursera-ui/svg';

import { invalidateDates, validateDates } from 'bundles/author-sessions/actions/SessionItemActions';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import { hasValidAvailabilityDates } from 'bundles/author-sessions/utils/validationUtils';
import { FloatingTooltip } from 'bundles/common/components/Tooltip';

import _t from 'i18n!nls/author-sessions';

import 'css!./__styles__/AvailabilityPicker';

const minimumVerticalDistance = 350;

type Dates = {
  startDate: moment.Moment;
  endDate: moment.Moment;
  maxDate: moment.Moment;
  itemDeadline: moment.Moment;
};

type Props = Dates & {
  minDate: moment.Moment;
  onChange: (x1: Partial<Dates>) => void;
  onShow: () => any;
  onHide: () => any;
  itemId: string;
  sessionId: string;
  item: SessionItem;
  readOnly?: boolean;
  openDirection?: string;
  dropDirection?: string;
  isClearable?: boolean;
  getClientYOnClick: (event: $TSFixMe) => void;
  clientY: number;
  parentEl?: string;
};

type State = {
  startDate?: moment.Moment | undefined;
  endDate?: moment.Moment | undefined;
  maxDate?: moment.Moment | undefined;
  itemDeadline?: moment.Moment | undefined;
  showPicker: boolean;
  invalidDate: boolean;
};

class AvailabilityPicker extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof AvailabilityPicker.contextTypes>;

  static defaultProps = {
    dropDirection: 'up',
    openDirection: 'center',
    isClearable: true,
  };

  constructor(props: Props) {
    import('css!bootstrap-daterangepicker/daterangepicker.css');
    super(props);
    this.state = {
      startDate: props.startDate,
      endDate: props.endDate,
      maxDate: props.maxDate,
      itemDeadline: props.itemDeadline || null,
      showPicker: false,
      invalidDate: false,
    };
  }

  componentDidMount() {
    const { startDate, endDate, itemDeadline } = this.state;

    const { maxDate } = this.props;

    if (startDate && endDate) {
      this.validateDates({ startDate, endDate, maxDate, itemDeadline });
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    // update deadline from props
    if (nextProps.itemDeadline && !nextProps.itemDeadline.isSame(this.props.itemDeadline, 'minute')) {
      const newItemDeadline = momentWithUserTimezone(nextProps.itemDeadline);
      this.updateDates({ itemDeadline: newItemDeadline }, true);
    }

    // update start date from props
    if (nextProps.startDate && !nextProps.startDate.isSame(this.props.startDate, 'minute')) {
      this.updateDates({ startDate: momentWithUserTimezone(nextProps.startDate) });
    }

    // update end date from props
    if (nextProps.endDate && !nextProps.endDate.isSame(this.props.endDate, 'minute')) {
      this.updateDates({ endDate: momentWithUserTimezone(nextProps.endDate) });
    }

    // new start date
    if (!this.props.startDate && nextProps.startDate) {
      this.updateDates({ startDate: momentWithUserTimezone(nextProps.startDate) });
    }

    // new end date
    if (!this.props.endDate && nextProps.endDate) {
      this.updateDates({ endDate: momentWithUserTimezone(nextProps.endDate) });
    }
  }

  noop() {}

  updateDates = (dates: Partial<Dates>, callOnChange = false) => {
    this.setState(dates, () => {
      const { startDate, endDate, maxDate, itemDeadline } = this.state;
      this.validateDates({ startDate, endDate, maxDate, itemDeadline }, callOnChange);
    });
  };

  validateDates = ({ startDate, endDate, itemDeadline }: Partial<Dates>, callOnChange = false) => {
    const { maxDate, sessionId, onChange } = this.props;
    const { executeAction } = this.context;
    // perform validations on currently set dates, and set the error/success states accordingly
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ startDate: any; endDate: any; ... Remove this comment to see the full error message
    if (hasValidAvailabilityDates({ startDate, endDate, maxDate, itemDeadline })) {
      this.setState({ startDate, endDate, invalidDate: false });
      executeAction(validateDates, { sessionId, validationType: 'availability' });

      if (callOnChange) {
        onChange({ startDate, endDate });
      }
    } else {
      this.setState({ startDate, endDate, invalidDate: true });
      executeAction(invalidateDates, {
        sessionId,
        message: 'Availability cannot end before deadline or start after deadline.',
        validationType: 'availability',
      });
    }
  };

  handleDateChange = () => {
    const { itemDeadline } = this.state;

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'pickerStartDate' does not exist on type ... Remove this comment to see the full error message
    const startDate = this.pickerStartDate;
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'pickerEndDate' does not exist on type 'A... Remove this comment to see the full error message
    const endDate = this.pickerEndDate;

    this.validateDates({ startDate, endDate, itemDeadline }, true);
  };

  handleClearDates = () => {
    this.setState({ showPicker: false });
    this.updateDates({ startDate: undefined, endDate: undefined }, true);
  };

  handleCustomDate = (date: moment.Moment, itemDeadline: moment.Moment) => {
    const { item, itemId } = this.props;
    const dateClasses: Array<string> = [];

    // get dates from the datepicker jquery object. we need this since the library does not provide
    // an event listener for when a date is selected, but only for when a date is applied using 'onApply'.
    // By pulling out the date from here, we can add custom styles at the moment of a new date selection.
    const dp = $(`#availability-picker-${itemId}`);

    if (!dp) {
      return null;
    }

    const pickerData = dp.parent().data('daterangepicker');
    this.addClassToOpenPicker(pickerData);

    const newStartDate = pickerData.startDate;
    const newEndDate = pickerData.endDate;
    const hardDeadline = item.isIncrementalLatePenalty && item.getHardDeadlineDate(itemDeadline);

    // set new start and end dates
    if (newStartDate && newEndDate) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'pickerStartDate' does not exist on type ... Remove this comment to see the full error message
      this.pickerStartDate = newStartDate;
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'pickerEndDate' does not exist on type 'A... Remove this comment to see the full error message
      this.pickerEndDate = newEndDate;
    }

    // only set custom styles if we have an item-level deadline
    if (!itemDeadline) {
      return dateClasses;
    }

    if (date.isSame(itemDeadline, 'day')) {
      dateClasses.push('deadline-date');
    } else if (date.isSame(hardDeadline, 'day')) {
      dateClasses.push('deadline-hard-date');
    } else if (date.isAfter(itemDeadline, 'minute') && date.isBefore(hardDeadline, 'minute')) {
      // any date between the deadline and hard deadline
      dateClasses.push('late-penalty-date');
    }

    if (newStartDate) {
      // selected start date is after deadline
      if (date.isSame(newStartDate, 'day') && date.isAfter(itemDeadline, 'minute')) {
        dateClasses.push('invalid-start-date');
      }
    }

    // a new end date is being selected
    if (itemDeadline && newStartDate && !newEndDate) {
      // disable all dates between the selected start date and the item deadline,
      // which forces users to automatically select a valid end date.
      if (date.isAfter(newStartDate, 'day') && date.isBefore(itemDeadline, 'day')) {
        dateClasses.push('disabled-window disabled off');
      }
    }

    setTimeout(() => {
      // force datepicker.move() to set the correct position when it is opened
      pickerData.move();
    }, 0);

    return dateClasses;
  };

  isSameAsSessionDuration = () => {
    const { startDate, endDate } = this.state;
    const { minDate, maxDate } = this.props;
    const isSameAsMinDate = moment(startDate).isSame(minDate, 'minute');
    const isSameAsMaxDate = moment(endDate).isSame(maxDate, 'minute');

    return isSameAsMinDate && isSameAsMaxDate;
  };

  removeDatePickerClassName = (className: string) => {
    const datePickerNodeList = document.querySelectorAll('.daterangepicker.dropdown-menu.ltr');

    datePickerNodeList.forEach((element) => {
      element.classList.remove(className);
    });
  };

  addClassToOpenPicker = (pickerData: $TSFixMe) => {
    const pickerContainer = pickerData.container;

    this.removeDatePickerClassName('open-picker');
    pickerContainer.addClass('open-picker');
  };

  renderAvailabilityDates(startDate: moment.Moment, endDate: moment.Moment) {
    const isSameAsSessionDuration = this.isSameAsSessionDuration();

    if (!startDate || !endDate || isSameAsSessionDuration) {
      return _t('Entire Session');
    }

    return (
      <div className="availability-dates">
        {`${formatDateTimeDisplay(startDate, MED_DATETIME_NO_YEAR_DISPLAY)} - ${formatDateTimeDisplay(
          endDate,
          MED_DATETIME_NO_YEAR_DISPLAY
        )}`}
        {this.renderClearAvailability()}
      </div>
    );
  }

  renderAvailability() {
    const { startDate, endDate } = this.state;
    const isSameAsSessionDuration = this.isSameAsSessionDuration();

    return (
      <div
        className="availability"
        onMouseEnter={() => this.setState({ showPicker: true })}
        onMouseLeave={() => this.setState({ showPicker: false })}
      >
        {!startDate || !endDate || isSameAsSessionDuration
          ? this.renderNoAvailability()
          : this.renderAvailabilityDates(startDate, endDate)}
      </div>
    );
  }

  renderNoAvailability() {
    const editItemAvailabilityLabel = _t('Edit Item Availability');
    return (
      <FloatingTooltip placement="bottom" message={editItemAvailabilityLabel}>
        <div
          className="no-availability"
          onMouseEnter={() => this.setState({ showPicker: true })}
          onMouseLeave={() => this.setState({ showPicker: false })}
        >
          {_t('Entire Session')}
        </div>
      </FloatingTooltip>
    );
  }

  renderClearAvailability() {
    const { isClearable } = this.props;

    if (!isClearable) {
      return null;
    }

    return (
      <SvgButton
        type="icon"
        size="sm"
        rootClassName="m-l-1s clear-availability-button"
        onClick={this.handleClearDates}
        isTransparent
        svgElement={<SvgFail color={color.secondaryText} suppressTitle={true} hoverColor={color.primary} size={12} />}
        htmlAttributes={{ title: _t('Clear dates') }}
      />
    );
  }

  renderReadOnly() {
    const { startDate, endDate } = this.state;
    const editItemAvailabilityLabel = _t('Editing not allowed until course launch');
    const isSameAsSessionDuration = this.isSameAsSessionDuration();

    return (
      <FloatingTooltip placement="bottom" message={editItemAvailabilityLabel}>
        <div className="horizontal-box">
          {(!startDate || !endDate || isSameAsSessionDuration) && _t('Entire Session')}
          {startDate &&
            endDate &&
            `${formatDateTimeDisplay(startDate, MED_DATETIME_NO_YEAR_DISPLAY)} - ${formatDateTimeDisplay(
              endDate,
              MED_DATETIME_NO_YEAR_DISPLAY
            )}`}
        </div>
      </FloatingTooltip>
    );
  }

  render() {
    const { startDate, endDate, showPicker, invalidDate, itemDeadline } = this.state;
    const {
      minDate,
      maxDate,
      itemId,
      onShow,
      onHide,
      readOnly,
      dropDirection,
      openDirection,
      clientY,
      getClientYOnClick,
      parentEl,
    } = this.props;
    const pickerDropDirection = clientY < minimumVerticalDistance ? 'down' : dropDirection;

    if (readOnly) {
      return this.renderReadOnly();
    }

    const wrapperClasses = classNames('rc-AvailabilityPicker horizontal-box', {
      'invalid-date': invalidDate,
    });

    const pickerInputClasses = classNames('picker-input', {
      'invalid-date': invalidDate,
    });

    const editItemAvailabilityLabel = invalidDate
      ? _t('Deadline must occur during the availability window.')
      : _t('Edit Item Availability');

    return (
      <div className={wrapperClasses}>
        {showPicker ? (
          <DateRangePicker
            startDate={startDate || minDate}
            endDate={endDate || maxDate}
            minDate={minDate}
            maxDate={maxDate}
            onApply={this.handleDateChange}
            onCancel={this.noop}
            onShow={onShow}
            onHide={onHide}
            opens={openDirection}
            drops={pickerDropDirection}
            applyClass="teach-btn primary m-r-1s"
            cancelClass="teach-btn secondary"
            timePicker={true}
            autoApply={true}
            isCustomDate={(date: moment.Moment) =>
              this.handleCustomDate(
                date,
                // @ts-expect-error
                itemDeadline
              )
            }
            parentEl={parentEl} // jQuery selector of parent element; DateRangePicker uses body by default if not provided
          >
            <FloatingTooltip placement="bottom" message={editItemAvailabilityLabel}>
              <div
                className={pickerInputClasses}
                id={`availability-picker-${itemId}`}
                role="button"
                tabIndex={-1} // added because the tabbing through the course session row automatically opens up the picker
                onClick={getClientYOnClick}
                onKeyDown={(event) => a11yKeyPress(event, getClientYOnClick)}
              >
                {this.renderAvailabilityDates(
                  // @ts-expect-error
                  startDate,
                  endDate
                )}
              </div>
            </FloatingTooltip>
          </DateRangePicker>
        ) : (
          this.renderAvailability()
        )}
      </div>
    );
  }
}

export default AvailabilityPicker;
